
import React, { useState } from 'react';
import { supabase } from '../supabase';
import { Loader2, Lock, Check, AlertCircle } from 'lucide-react';

interface SetPasswordModalProps {
  onComplete: () => void;
}

const SetPasswordModal: React.FC<SetPasswordModalProps> = ({ onComplete }) => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Las contraseñas no coinciden.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;
      
      setSuccess(true);
      setTimeout(() => {
        onComplete();
        // Recargar para limpiar el hash de la URL
        window.location.href = window.location.origin;
      }, 2000);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-dark-bg p-4">
      <div className="glass-card max-w-md w-full p-8 sm:p-12 border-brand/20 relative overflow-hidden shadow-[0_0_50px_rgba(255,255,255,0.05)]">
        <div className="absolute top-0 left-0 w-full h-1.5 bg-brand/20">
          <div className="h-full bg-brand animate-pulse" style={{ width: '100%' }}></div>
        </div>

        <div className="text-center mb-10">
          <div className="w-20 h-20 bg-brand/10 rounded-full flex items-center justify-center mx-auto mb-6 border border-brand/20 shadow-[0_0_30px_rgba(255,255,255,0.1)]">
            <Lock className="text-brand" size={40} />
          </div>
          <h2 className="text-3xl font-display uppercase tracking-tighter text-white">Nueva Contraseña</h2>
          <p className="text-white/40 text-[11px] uppercase tracking-[0.3em] font-bold mt-3">Elite Access Recovery</p>
        </div>

        {success ? (
          <div className="text-center py-12 animate-in fade-in zoom-in duration-500">
            <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-6 border border-emerald-500/30">
              <Check className="text-emerald-500" size={32} />
            </div>
            <p className="text-white font-display uppercase tracking-widest text-lg">¡Contraseña establecida!</p>
            <p className="text-white/40 text-[10px] mt-4 uppercase tracking-[0.2em]">Redirigiendo al portal...</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-8">
            <div className="space-y-3">
              <label className="text-[11px] font-bold text-white/30 uppercase tracking-[0.3em] ml-1">Nueva Contraseña</label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-white/20" size={20} />
                <input 
                  type="password" 
                  placeholder="••••••••" 
                  className="luxury-input w-full pl-12 py-4"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoFocus
                />
              </div>
            </div>

            <div className="space-y-3">
              <label className="text-[11px] font-bold text-white/30 uppercase tracking-[0.3em] ml-1">Confirmar Contraseña</label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-white/20" size={20} />
                <input 
                  type="password" 
                  placeholder="••••••••" 
                  className="luxury-input w-full pl-12 py-4"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
              </div>
            </div>

            {error && (
              <div className="flex items-center gap-4 p-5 bg-red-500/10 border border-red-500/20 rounded-2xl text-red-400 text-[11px] font-bold uppercase tracking-wider">
                <AlertCircle size={20} className="shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <button 
              type="submit" 
              disabled={loading}
              className="brutal-btn w-full flex items-center justify-center gap-4 py-5 text-sm disabled:opacity-50"
            >
              {loading ? <Loader2 className="animate-spin" size={24} /> : 'Guardar Nueva Clave'}
              {!loading && <Check size={20} />}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default SetPasswordModal;
